import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Minus, Plus, X } from "lucide-react";
import { toast } from "sonner";
import ProductVisual, { Sprig } from "../components/ProductVisual";
import { Reveal } from "../components/Reveal";
import { formatINR, FREE_DELIVERY_THRESHOLD, CLINIC } from "../data/catalog";
import { useCart } from "../context/CartContext";
import { WhatsAppGlyph } from "../components/WhatsAppFloat";

const Thumb = ({ item }) =>
  item.kind === "set" || !item.images?.length ? (
    <ProductVisual product={item} className="h-24 w-20 shrink-0" />
  ) : (
    <div className="h-24 w-20 shrink-0 overflow-hidden bg-[#EFEAE0]">
      <img src={item.images[0]} alt={item.name} className="h-full w-full object-cover" />
    </div>
  );

const CartPage = () => {
  const { items, setQty, remove, add, subtotal, delivery, total, coupon, applyCoupon, removeCoupon, discount, resolve, catalogue } = useCart();
  const [couponOpen, setCouponOpen] = useState(false);
  const [code, setCode] = useState("");

  const away = Math.max(0, FREE_DELIVERY_THRESHOLD - (subtotal - discount));
  const crossSell = catalogue.filter((p) => p.kind === "product" && (p.entry || p.bestseller) && !items.find((i) => i.id === p.id)).slice(0, 2);

  const tryCoupon = () => {
    if (!code.trim()) return;
    if (applyCoupon(code)) {
      toast.success("Code applied", { description: "10% off this order." });
      setCode("");
    } else {
      toast.error("That code isn't working", { description: "Check the spelling, or message us." });
    }
  };

  if (items.length === 0) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center px-4 pb-28 pt-44 text-center" data-testid="cart-empty">
        <Sprig className="h-14 w-auto text-sage-deep" />
        <h1 className="mt-8 font-serif text-4xl font-light italic md:text-5xl">Your cart is empty.</h1>
        <p className="mt-4 max-w-sm text-sm leading-relaxed text-ink/60">
          Ten products, made by a doctor in Thiruvananthapuram — have a look.
        </p>
        <Link to="/shop" data-testid="cart-empty-shop-cta" className="mt-10 inline-flex items-center gap-3 rounded-full bg-ink px-8 py-4 text-[13px] uppercase tracking-[0.2em] text-cream transition-colors hover:bg-ink-deep">
          Shop the Range <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 pb-24 pt-36 md:px-8 md:pt-44" data-testid="cart-page">
      <Reveal>
        <p className="eyebrow">Your cart</p>
        <h1 className="mt-4 font-serif text-4xl font-light md:text-5xl">
          Almost <span className="italic text-sage-deep">yours.</span>
        </h1>
      </Reveal>

      <div className="mt-12 grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <div className="border-t hairline">
            {items.map((line) => {
              const item = resolve(line.id);
              if (!item) return null;
              return (
                <motion.div key={line.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-5 border-b hairline py-6" data-testid={`cart-line-${line.id}`}>
                  <Thumb item={item} />
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-serif text-lg leading-snug">{item.name}</h3>
                        <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-ink/45">
                          {item.size || item.contents?.join(" · ")} · {formatINR(item.price)}
                        </p>
                      </div>
                      <button onClick={() => remove(line.id)} data-testid={`cart-remove-${line.id}`} aria-label={`Remove ${item.name}`} className="p-1 text-ink/40 transition-colors hover:text-ink">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-auto flex items-center justify-between pt-4">
                      <div className="flex items-center border hairline">
                        <button onClick={() => setQty(line.id, line.qty - 1)} data-testid={`cart-qty-minus-${line.id}`} aria-label="Decrease" className="flex h-9 w-9 items-center justify-center transition-colors hover:bg-cream-2">
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-8 text-center text-sm tabular-nums" data-testid={`cart-qty-${line.id}`}>{line.qty}</span>
                        <button onClick={() => setQty(line.id, line.qty + 1)} data-testid={`cart-qty-plus-${line.id}`} aria-label="Increase" className="flex h-9 w-9 items-center justify-center transition-colors hover:bg-cream-2">
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <p className="font-medium tabular-nums">{formatINR(item.price * line.qty)}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {crossSell.length > 0 && (
            <div className="mt-10" data-testid="cross-sell">
              <p className="eyebrow">Goes well with</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {crossSell.map((p) => (
                  <div key={p.id} className="flex items-center gap-4 border hairline bg-white/60 p-4">
                    <div className="h-16 w-14 shrink-0 overflow-hidden bg-[#EFEAE0]">
                      {p.images?.length ? (
                        <img src={p.images[0]} alt={p.name} className="h-full w-full object-cover" />
                      ) : (
                        <ProductVisual product={p} className="h-full w-full" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-serif text-[15px] leading-tight">{p.name}</p>
                      <p className="mt-1 text-[12px] text-ink/50">{formatINR(p.price)}</p>
                    </div>
                    <button onClick={() => add(p.id, 1)} data-testid={`cross-sell-add-${p.slug}`} className="rounded-full border border-ink/30 px-4 py-2 text-[10px] uppercase tracking-[0.16em] transition-colors hover:bg-ink hover:text-cream">
                      Add
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-5">
          <div className="border hairline bg-white/60 p-8" data-testid="cart-summary">
            <h2 className="font-serif text-2xl italic">Order summary</h2>

            <div className="mt-6">
              <p className="text-[13px] leading-relaxed text-ink/70" data-testid="delivery-progress-label">
                {away > 0 ? `Add ${formatINR(away)} more for free delivery across India.` : "Free delivery unlocked."}
              </p>
              <div className="mt-3 h-1 w-full bg-ink/10">
                <motion.div
                  className="h-full bg-sage-deep"
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(100, ((subtotal - discount) / FREE_DELIVERY_THRESHOLD) * 100)}%` }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                  data-testid="delivery-progress-bar"
                />
              </div>
            </div>

            <div className="mt-6 border-t hairline pt-5">
              {coupon ? (
                <div className="flex items-center justify-between text-sm" data-testid="coupon-applied">
                  <p className="text-sage-deep">{coupon.code} — {coupon.pct}% off</p>
                  <button onClick={removeCoupon} data-testid="coupon-remove" className="text-[11px] uppercase tracking-[0.16em] text-ink/50 hover:text-ink">Remove</button>
                </div>
              ) : couponOpen ? (
                <div className="flex gap-2">
                  <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter code" data-testid="coupon-input" className="flex-1 border hairline bg-cream px-4 py-2.5 text-sm placeholder:text-ink/30" />
                  <button onClick={tryCoupon} data-testid="coupon-apply" className="border hairline px-5 text-[11px] uppercase tracking-[0.16em] transition-colors hover:bg-cream-2">Apply</button>
                </div>
              ) : (
                <button onClick={() => setCouponOpen(true)} data-testid="coupon-open" className="text-[12px] uppercase tracking-[0.18em] text-ink/55 transition-colors hover:text-ink">
                  Have a code?
                </button>
              )}
            </div>

            <dl className="mt-6 space-y-3 border-t hairline pt-6 text-sm">
              <div className="flex justify-between">
                <dt className="text-ink/60">Subtotal</dt>
                <dd className="tabular-nums" data-testid="cart-subtotal">{formatINR(subtotal)}</dd>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-sage-deep">
                  <dt>Coupon</dt>
                  <dd className="tabular-nums" data-testid="cart-discount">−{formatINR(discount)}</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-ink/60">Delivery</dt>
                <dd className="tabular-nums" data-testid="cart-delivery">{delivery === 0 ? "Free" : formatINR(delivery)}</dd>
              </div>
              <div className="flex justify-between border-t hairline pt-4 font-serif text-xl">
                <dt>Total</dt>
                <dd className="tabular-nums" data-testid="cart-total">{formatINR(total)}</dd>
              </div>
              <p className="text-[11px] text-ink/40">All prices inclusive of taxes.</p>
            </dl>

            <Link to="/checkout" data-testid="checkout-cta" className="mt-8 flex items-center justify-center gap-3 rounded-full bg-ink px-8 py-4 text-[13px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-ink-deep">
              Checkout <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/shop" data-testid="continue-shopping" className="mt-4 block text-center text-[12px] uppercase tracking-[0.18em] text-ink/55 transition-colors hover:text-ink">
              Continue shopping
            </Link>
            <a href={CLINIC.whatsapp} target="_blank" rel="noreferrer" data-testid="cart-whatsapp-cta" className="mt-6 flex items-center justify-center gap-2 border-t hairline pt-6 text-[12px] uppercase tracking-[0.18em] text-ink/60 transition-colors hover:text-ink">
              <WhatsAppGlyph className="h-4 w-4" /> Questions? WhatsApp {CLINIC.phone}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
